// src/stores/useTestStore.ts
// Zustand store for the Test system.
//
// Mirrors useExerciseStore's API surface:
//   • startTest(bookId, unitNum, version) → { ok, reason?, message? }
//     – exact same StartUnitResult-style discriminated union.
//   • currentBookId/currentUnitNum/currentVersion expose "is a test active",
//     analogous to useExerciseStore.currentUnitId.
//   • TestPage.tsx uses these to redirect when no test is loaded.
//
// All transient UI state (selected option highlight, feedback panel) lives
// in the TestSession component. The store keeps the canonical answer state
// and the question/result data — these need to outlast a re-mount and feed
// into the results screen.

import { create } from 'zustand';
import { useAuthStore } from './useAuthStore';
import { canAccessUnit } from '../services/paymentService';
import {
  buildQuestionPool,
  loadPreviousBest,
  loadTestData,
  saveTestProgress,
} from '../services/testService';
import { useStreakStore } from './useStreakStore';
import type {
  PreviousBest,
  QuestionResult,
  StartTestResult,
  TestData,
  TestQuestion,
} from '../types/test';

/** The phase of the current test session. */
export type TestPhase =
  | 'idle'        // no test loaded
  | 'loading'    // fetching JSON / previous best
  | 'active'     // user answering questions
  | 'finished'   // user reached the end (pass or fail)
  | 'early_stop' // mathematically impossible to pass
  | 'error';     // load failed

interface TestStoreState {
  // Identity
  currentBookId: string | null;
  currentUnitNum: string | null;
  currentVersion: string | null;

  // Data
  testData: TestData | null;
  questions: TestQuestion[];
  previousBest: PreviousBest | null;

  // Live answer state
  currentIndex: number;
  answers: Record<string, string>;       // qId → selected option key
  results: Record<string, QuestionResult>;
  score: number;
  startTime: number | null;

  // Status
  phase: TestPhase;
  errorMessage: string | null;

  // ── Actions ──────────────────────────────────────────────────────────────
  startTest: (bookId: string, unitNum: string, version: string) => Promise<StartTestResult>;
  selectAnswer: (questionId: string, optionKey: string) => void;
  /** Submits the current question. Returns the next phase the UI should observe. */
  submitAnswer: () => 'next' | 'finished' | 'early_stop';
  retake: () => void;
  reset: () => void;
}

const INITIAL = {
  currentBookId: null,
  currentUnitNum: null,
  currentVersion: null,
  testData: null,
  questions: [] as TestQuestion[],
  previousBest: null,
  currentIndex: 0,
  answers: {} as Record<string, string>,
  results: {} as Record<string, QuestionResult>,
  score: 0,
  startTime: null as number | null,
  phase: 'idle' as TestPhase,
  errorMessage: null as string | null,
};

export const useTestStore = create<TestStoreState>((set, get) => ({
  ...INITIAL,

  // ── startTest ─────────────────────────────────────────────────────────────
  startTest: async (bookId, unitNum, version) => {
    // Auth check (mirrors UnitCard.handleStart for exercises).
    const { user } = useAuthStore.getState();
    if (!user) {
      return { ok: false, reason: 'login_required' };
    }

    // Subscription gate.
    // We can't import useSubscription here (it's a hook). The payment store
    // owns the truth — this matches the existing pattern in usePaymentStore.
    try {
      const hasAccess = await canAccessUnit(bookId, `unit${unitNum}`);
      if (!hasAccess) {
        return { ok: false, reason: 'access_denied' };
      }
    } catch {
      // If canAccessUnit isn't wired up yet, fall through — UnitCard's gate
      // already runs before this is reached in production.
    }

    set({
      ...INITIAL,
      currentBookId: bookId,
      currentUnitNum: unitNum,
      currentVersion: version,
      phase: 'loading',
    });

    let testData: TestData | null;
    try {
      testData = await loadTestData(bookId, unitNum, version);
    } catch (err) {
      console.error('[useTestStore] loadTestData threw:', err);
      testData = null;
    }

    if (!testData) {
      set({
        phase: 'error',
        errorMessage: `Could not load Test ${version} for Unit ${unitNum}. Please upload via admin panel.`,
      });
      return {
        ok: false,
        reason: 'load_failed',
        message: `Could not load Test ${version} for Unit ${unitNum}.`,
      };
    }

    const questions = buildQuestionPool(testData);
    if (questions.length === 0) {
      set({
        phase: 'error',
        errorMessage: 'This test has no questions.',
      });
      return { ok: false, reason: 'load_failed', message: 'Test has no questions.' };
    }

    const previousBest = await loadPreviousBest(bookId, unitNum, version);

    set({
      testData,
      questions,
      previousBest,
      currentIndex: 0,
      answers: {},
      results: {},
      score: 0,
      startTime: Date.now(),
      phase: 'active',
      errorMessage: null,
    });

    return { ok: true };
  },

  // ── selectAnswer ──────────────────────────────────────────────────────────
  selectAnswer: (questionId, optionKey) => {
    set((state) => ({ answers: { ...state.answers, [questionId]: optionKey } }));
  },

  // ── submitAnswer ──────────────────────────────────────────────────────────
  submitAnswer: () => {
    const state = get();
    if (state.phase !== 'active') return 'next';

    const q = state.questions[state.currentIndex];
    if (!q) return 'next';

    const selected = state.answers[q.id];
    if (!selected) return 'next';

    const isCorrect = selected === q.correctAnswer;
    const result: QuestionResult = {
      correct: isCorrect,
      selected,
      correctAnswer: q.correctAnswer,
      question: q.question,
      options: q.options,
      explanation: q.explanation,
      sectionTitle: q.sectionTitle,
    };

    const newResults = { ...state.results, [q.id]: result };
    const newScore = isCorrect ? state.score + 1 : state.score;

    // Early-stop check — same math as test-manager._checkEarlyStop().
    const total = state.questions.length;
    const answered = Object.keys(newResults).length;
    const remaining = total - answered;
    const passing = (state.testData?.metadata.passingScore ?? 80) / 100;
    const maxPossible = newScore + remaining;

    if (maxPossible / total < passing) {
      set({
        results: newResults,
        score: newScore,
        phase: 'early_stop',
      });
      void persistResult(get(), false);
      return 'early_stop';
    }

    // End reached normally.
    if (state.currentIndex + 1 >= total) {
      const pct = Math.round((newScore / total) * 100);
      const passed = pct >= (state.testData?.metadata.passingScore ?? 80);
      set({
        results: newResults,
        score: newScore,
        phase: 'finished',
      });
      void persistResult(get(), passed);
      return 'finished';
    }

    // Advance.
    set({
      results: newResults,
      score: newScore,
      currentIndex: state.currentIndex + 1,
    });
    return 'next';
  },

  // ── retake ────────────────────────────────────────────────────────────────
  retake: () => {
    const state = get();
    if (!state.testData) return;
    const questions = buildQuestionPool(state.testData);
    set({
      questions,
      currentIndex: 0,
      answers: {},
      results: {},
      score: 0,
      startTime: Date.now(),
      phase: 'active',
      errorMessage: null,
    });
  },

  // ── reset ─────────────────────────────────────────────────────────────────
  reset: () => set({ ...INITIAL }),
}));

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Persist the result and bump the streak. Called for both 'finished' and
 * 'early_stop' phases — failed/incomplete tests still write progress (with
 * passed=false) so the unit ring reflects the attempt.
 */
async function persistResult(state: TestStoreState, passed: boolean): Promise<void> {
  const { currentBookId, currentUnitNum, currentVersion, questions, results, startTime } = state;
  if (!currentBookId || !currentUnitNum || !currentVersion || !startTime) return;

  const total = questions.length;
  const answered = Object.keys(results).length;
  const correct = Object.values(results).filter((r) => r.correct).length;

  // For early-stop we report the partial percentage on what was answered.
  // For a finished test we report the full percentage on the question pool.
  const denom = state.phase === 'finished' ? total : Math.max(answered, 1);
  const numer = state.phase === 'finished' ? correct : correct;
  const pct = Math.round((numer / denom) * 100);
  const elapsed = Math.round((Date.now() - startTime) / 1000);

  try {
    await saveTestProgress({
      bookId: currentBookId,
      unitNum: currentUnitNum,
      version: currentVersion,
      score: correct,
      maxScore: total,
      percentage: pct,
      passed,
      elapsedSeconds: elapsed,
    });
  } catch (err) {
    console.error('[useTestStore] saveTestProgress failed:', err);
  }

  // Streak counts the day for any meaningful test attempt (answered ≥ 1).
  if (answered >= 1) {
    try {
      await useStreakStore.getState().recordActivity();
    } catch (err) {
      console.warn('[useTestStore] streak bump failed:', err);
    }
  }
}